// The four front ends are path-routed behind one host (`/auth`, `/admin`, `/inspector`,
// `/report`), and `auth-package` builds `returnUrl` from another app's `pathname`, so every
// legitimate target is a same-origin path. An absolute or protocol-relative URL is therefore
// never ours to follow: `//evil.com` would take a session off-site.

/**
 * Resolves a raw `returnUrl` to a safe, origin-locked absolute URL, or `null` when it is not safe to
 * follow. Parsing against the current origin (rather than string-matching the raw value) is what
 * closes the redirect bypasses: the browser treats `\` as `/` and strips tab/newline, so a raw check
 * of "starts with `/` but not `//`" lets `/%5Cevil.com` and `/%09/evil.com` through — both resolve to
 * `//evil.com` off-origin when assigned to `location`. `new URL(value, origin)` keeps those encoded,
 * so the `origin` comparison rejects anything that would leave the site, and returning the absolute
 * `url.href` gives callers a value that cannot be re-parsed off-origin at the navigation sink.
 *
 * Rejects any `/auth/*` target so a return address can never bounce back into the auth app (login
 * loop or ceremony re-entry with a spent token).
 */
export const resolveSafeReturnUrl = (returnUrl: string | null | undefined): string | null => {
  if (!returnUrl) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(returnUrl, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) {
    return null;
  }

  if (url.pathname === '/auth' || url.pathname.startsWith('/auth/')) {
    return null;
  }

  return url.href;
};

/**
 * A `returnUrl` is safe to redirect to only when {@link resolveSafeReturnUrl} accepts it: a
 * same-origin target that does not point back into the auth app. Shared by the post-adopt success
 * redirect and the `/mfa` / `/switch` exits. Validation happens once, at the redirect.
 */
export const isSafeReturnUrl = (returnUrl: string | null | undefined): returnUrl is string =>
  resolveSafeReturnUrl(returnUrl) !== null;
